import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpHelpService } from '../service/http-help.service';
import { CurrencyService } from '../../services/currency.service';
import { CurrencyDto, DEFAULT_CURRENCY_CODE } from '../../models/currency.model';

export interface CustomerAccountEntryRow {
  entry_id: number;
  company_id: number;
  customer_id: number;
  entry_date: string;
  amount: number;
  entry_type: 'debit' | 'credit';
  currency?: string;
  note?: string;
  customer?: {
    customer_name_ar?: string;
    customer_name_en?: string;
    customer_name_he?: string;
    customer_name?: string;
  };
}

@Component({
  selector: 'app-customer-account-entries',
  templateUrl: './customer-account-entries.component.html',
  styleUrls: ['./customer-account-entries.component.scss'],
})
export class CustomerAccountEntriesComponent implements OnInit, OnDestroy {
  curentLang = 'ar';
  companyId: number | null = null;
  companyData: { company_id: number; label: string }[] = [];
  customerFilterId: number | null = null;
  customersForFilter: { customer_id: number; label: string }[] = [];
  customersForForm: { customer_id: number; label: string }[] = [];

  fromDate: Date | null = null;
  toDate: Date | null = null;
  dateRangeError = '';

  entries: CustomerAccountEntryRow[] = [];
  loading = false;

  formVisible = false;
  editingEntryId: number | null = null;
  saving = false;

  currencyOptions: { code: string; label: string }[] = [
    { code: DEFAULT_CURRENCY_CODE, label: DEFAULT_CURRENCY_CODE },
  ];
  currenciesLoading = false;

  entryForm = new FormGroup({
    company_id: new FormControl<number | null>(null, Validators.required),
    customer_id: new FormControl<number | null>(null, Validators.required),
    entry_date: new FormControl<Date | null>(null, Validators.required),
    entry_type: new FormControl<'debit' | 'credit'>('debit', Validators.required),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    currency: new FormControl(DEFAULT_CURRENCY_CODE, Validators.required),
    note: new FormControl('', Validators.maxLength(1000)),
  });

  private destroy$ = new Subject<void>();
  private onCompanyChangedHandler: EventListener | null = null;
  private dateReloadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private service: HttpHelpService,
    private currencyService: CurrencyService,
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'ar';
    this.initializeDateRange();
    this.loadCompanyId();
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.curentLang = event.lang;
      this.refreshCustomerLabels();
      this.refreshCurrencyLabels();
      if (this.companyId) {
        this.loadEntries();
      }
    });

    this.curentLang = this.translate.currentLang || this.curentLang;
    this.loadCompanies();
    this.loadCurrencies();

    this.onCompanyChangedHandler = ((e: Event) => {
      const id = (e as CustomEvent<{ companyId?: number }>).detail?.companyId;
      if (id != null) {
        this.companyId = Number(id);
        this.customerFilterId = null;
        this.loadCustomersForFilter();
        this.loadEntries();
      }
    }) as EventListener;
    window.addEventListener('companyChanged', this.onCompanyChangedHandler);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.dateReloadTimer) {
      clearTimeout(this.dateReloadTimer);
      this.dateReloadTimer = null;
    }
    if (this.onCompanyChangedHandler) {
      window.removeEventListener('companyChanged', this.onCompanyChangedHandler);
    }
  }

  initializeDateRange(): void {
    const year = new Date().getFullYear();
    this.fromDate = new Date(year, 0, 1);
    this.toDate = new Date(year, 11, 31);
  }

  private loadCompanyId(): void {
    const stored = localStorage.getItem('companyDefault');
    this.companyId = stored ? Number(stored) : null;
  }

  private loadCompanies(): void {
    // MD: GET /company/getALLCompanyByBasedToken
    this.service.getAllCompaniesInfo(50, 0).subscribe({
      next: (res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.companyData = list.map((c: any) => ({
          company_id: c.company_id,
          label: this.getCompanyLabel(c),
        }));
        if (!this.companyId && this.companyData.length) {
          this.companyId = this.companyData[0].company_id;
        }
        if (this.companyId) {
          this.loadCustomersForFilter();
          this.loadEntries();
        }
      },
      error: () => {
        this.companyData = [];
      },
    });
  }

  private getCompanyLabel(c: any): string {
    if (this.curentLang === 'en') {
      return c.company_name_en || c.company_name_ar || String(c.company_id);
    }
    if (this.curentLang === 'he') {
      return c.company_name_he || c.company_name_ar || String(c.company_id);
    }
    return c.company_name_ar || c.company_name_en || String(c.company_id);
  }

  onCompanyChange(): void {
    this.customerFilterId = null;
    this.loadCustomersForFilter();
    this.loadEntries();
  }

  onCustomerFilterChange(): void {
    this.loadEntries();
  }

  onDateChange(): void {
    this.dateRangeError = '';
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.dateRangeError = this.translate.instant('customerAccountEntries.errors.invalidDateRange');
      return;
    }
    if (this.dateReloadTimer) {
      clearTimeout(this.dateReloadTimer);
    }
    this.dateReloadTimer = setTimeout(() => {
      if (this.companyId && this.validateDateRange()) {
        this.loadEntries();
      }
    }, 250);
  }

  private loadCurrencies(): void {
    this.currenciesLoading = true;
    this.currencyService.getActiveCurrencies().subscribe({
      next: (list) => {
        const currencies = [...(list || [])].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        );
        if (!currencies.some((c) => c.currency_code === DEFAULT_CURRENCY_CODE)) {
          currencies.unshift({
            currency_code: DEFAULT_CURRENCY_CODE,
            currency_symbol: '₪',
            name_en: 'Israeli Shekel',
            name_ar: 'شيكل',
            name_he: 'שקל',
            is_active: true,
            decimal_places: 2,
            sort_order: 0,
          } as CurrencyDto);
        }
        this.currencyOptions = currencies.map((c) => ({
          code: c.currency_code,
          label: this.currencyLabel(c),
        }));
        this.ensureCurrencyInOptions(this.entryForm.get('currency')?.value);
        this.currenciesLoading = false;
      },
      error: () => {
        this.currencyOptions = [{ code: DEFAULT_CURRENCY_CODE, label: DEFAULT_CURRENCY_CODE }];
        this.currenciesLoading = false;
      },
    });
  }

  private currencyLabel(c: CurrencyDto): string {
    const name =
      this.curentLang === 'ar'
        ? c.name_ar || c.name_en || c.name_he
        : this.curentLang === 'he'
          ? c.name_he || c.name_en || c.name_ar
          : c.name_en || c.name_he || c.name_ar;
    const symbol = c.currency_symbol ? ` ${c.currency_symbol}` : '';
    return `${c.currency_code}${symbol}${name ? ` — ${name}` : ''}`;
  }

  private refreshCurrencyLabels(): void {
    // Reload labels from API list if needed — keep codes, rebuild display via loadCurrencies
    this.loadCurrencies();
  }

  private ensureCurrencyInOptions(code: string | null | undefined): void {
    const value = (code || DEFAULT_CURRENCY_CODE).trim().toUpperCase() || DEFAULT_CURRENCY_CODE;
    if (!this.currencyOptions.some((c) => c.code === value)) {
      this.currencyOptions = [...this.currencyOptions, { code: value, label: value }];
    }
    if (!this.entryForm.get('currency')?.value) {
      this.entryForm.patchValue({ currency: DEFAULT_CURRENCY_CODE }, { emitEvent: false });
    }
  }

  loadCustomersForFilter(): void {
    if (!this.companyId) {
      this.customersForFilter = [];
      return;
    }
    this.service.getCustomersByCompanyId(this.companyId, 500, 0).subscribe({
      next: (customers) => {
        this.customersForFilter = (customers || []).map((c: any) => ({
          customer_id: c.customer_id ?? c.user_id,
          label: this.getCustomerLabel(c),
        }));
      },
      error: () => {
        this.customersForFilter = [];
      },
    });
  }

  loadCustomersForForm(companyId: number): void {
    this.service.getCustomersByCompanyId(companyId, 500, 0).subscribe({
      next: (customers) => {
        this.customersForForm = (customers || []).map((c: any) => ({
          customer_id: c.customer_id ?? c.user_id,
          label: this.getCustomerLabel(c),
        }));
      },
      error: () => {
        this.customersForForm = [];
      },
    });
  }

  private refreshCustomerLabels(): void {
    this.customersForFilter = this.customersForFilter.map((c) => {
      const raw = this.entries.find((e) => e.customer_id === c.customer_id)?.customer;
      return raw ? { ...c, label: this.getCustomerLabel(raw) } : c;
    });
  }

  getCustomerLabel(c: any): string {
    if (!c) return '—';
    if (this.curentLang === 'en') {
      return c.customer_name_en || c.customer_name || c.customer_name_ar || String(c.customer_id ?? '');
    }
    if (this.curentLang === 'he') {
      return c.customer_name_he || c.customer_name_ar || c.customer_name || String(c.customer_id ?? '');
    }
    return c.customer_name_ar || c.customer_name || c.customer_name_en || String(c.customer_id ?? '');
  }

  entryCustomerName(entry: CustomerAccountEntryRow): string {
    return this.getCustomerLabel(entry.customer || { customer_id: entry.customer_id });
  }

  entryTypeLabel(type: 'debit' | 'credit'): string {
    return this.translate.instant(
      type === 'debit' ? 'customerAccountEntries.entryTypes.debit' : 'customerAccountEntries.entryTypes.credit',
    );
  }

  loadEntries(): void {
    if (!this.companyId) {
      this.entries = [];
      return;
    }
    if (!this.validateDateRange()) {
      return;
    }

    this.loading = true;
    this.spinner.show();

    const params: {
      company_id: number;
      customer_id?: number;
      startDate?: string;
      endDate?: string;
    } = { company_id: this.companyId };

    if (this.customerFilterId != null) {
      params.customer_id = this.customerFilterId;
    }
    if (this.fromDate) {
      params.startDate = this.toApiDate(this.fromDate);
    }
    if (this.toDate) {
      params.endDate = this.toApiDate(this.toDate);
    }

    this.service.getCustomerAccountEntries(params).subscribe({
      next: (res) => {
        this.entries = Array.isArray(res?.data) ? res.data : [];
        this.loading = false;
        this.spinner.hide();
      },
      error: (err) => {
        this.entries = [];
        this.loading = false;
        this.spinner.hide();
        const msg =
          this.curentLang === 'ar'
            ? err?.error?.messageAr
            : this.curentLang === 'he'
              ? err?.error?.messageHe
              : err?.error?.messageEn;
        if (msg) {
          Swal.fire({ icon: 'error', title: msg });
        }
      },
    });
  }

  openCreateForm(): void {
    if (!this.companyId) {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('customerAccountEntries.warnings.no_company'),
      });
      return;
    }
    this.editingEntryId = null;
    this.entryForm.reset({
      company_id: this.companyId,
      customer_id: null,
      entry_date: new Date(),
      entry_type: 'debit',
      amount: null,
      currency: DEFAULT_CURRENCY_CODE,
      note: '',
    });
    this.entryForm.get('company_id')?.enable();
    this.entryForm.get('customer_id')?.enable();
    this.ensureCurrencyInOptions(DEFAULT_CURRENCY_CODE);
    this.loadCustomersForForm(this.companyId);
    this.formVisible = true;
  }

  openEditForm(entry: CustomerAccountEntryRow): void {
    this.editingEntryId = entry.entry_id;
    this.formVisible = true;
    this.entryForm.get('company_id')?.disable();
    this.entryForm.get('customer_id')?.disable();
    this.spinner.show();

    this.service.getCustomerAccountEntryById(entry.entry_id).subscribe({
      next: (res) => {
        this.spinner.hide();
        const data: CustomerAccountEntryRow =
          res?.data && typeof res.data === 'object' ? res.data : entry;
        this.patchEditForm(data);
      },
      error: () => {
        this.spinner.hide();
        this.patchEditForm(entry);
      },
    });
  }

  private patchEditForm(entry: CustomerAccountEntryRow): void {
    this.editingEntryId = entry.entry_id;
    const dateParts = entry.entry_date?.split('-');
    let entryDate: Date | null = null;
    if (dateParts?.length === 3) {
      entryDate = new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2]);
    }
    this.entryForm.patchValue({
      company_id: entry.company_id,
      customer_id: entry.customer_id,
      entry_date: entryDate,
      entry_type: entry.entry_type,
      amount: entry.amount,
      currency: entry.currency || DEFAULT_CURRENCY_CODE,
      note: entry.note || '',
    });
    this.ensureCurrencyInOptions(entry.currency || DEFAULT_CURRENCY_CODE);
    this.loadCustomersForForm(entry.company_id);
  }

  closeForm(): void {
    this.formVisible = false;
    this.editingEntryId = null;
    this.entryForm.get('company_id')?.enable();
    this.entryForm.get('customer_id')?.enable();
  }

  onFormCompanyChange(): void {
    const companyId = this.entryForm.get('company_id')?.value;
    this.entryForm.patchValue({ customer_id: null });
    if (companyId) {
      this.loadCustomersForForm(companyId);
    } else {
      this.customersForForm = [];
    }
  }

  saveEntry(): void {
    if (this.entryForm.invalid) {
      this.entryForm.markAllAsTouched();
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('customerAccountEntries.errors.requiredFields'),
      });
      return;
    }

    const raw = this.entryForm.getRawValue();
    const amount = Number(raw.amount);
    if (!amount || amount <= 0) {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('customerAccountEntries.errors.amountPositive'),
      });
      return;
    }

    const entryDate = raw.entry_date as Date;
    if (!entryDate) {
      return;
    }

    this.saving = true;
    this.spinner.show();

    if (this.editingEntryId != null) {
      const body = {
        entry_date: this.toApiDate(entryDate),
        amount: Math.round(amount * 100) / 100,
        entry_type: raw.entry_type as 'debit' | 'credit',
        currency: (raw.currency || DEFAULT_CURRENCY_CODE).trim().toUpperCase() || DEFAULT_CURRENCY_CODE,
        note: (raw.note || '').trim() || undefined,
      };
      this.service.updateCustomerAccountEntry(this.editingEntryId, body).subscribe({
        next: (res) => this.handleSaveResponse(res),
        error: (err) => this.handleSaveError(err),
      });
    } else {
      const body = {
        company_id: Number(raw.company_id),
        customer_id: Number(raw.customer_id),
        entry_date: this.toApiDate(entryDate),
        amount: Math.round(amount * 100) / 100,
        entry_type: raw.entry_type as 'debit' | 'credit',
        currency: (raw.currency || DEFAULT_CURRENCY_CODE).trim().toUpperCase() || DEFAULT_CURRENCY_CODE,
        note: (raw.note || '').trim() || undefined,
      };
      this.service.createCustomerAccountEntry(body).subscribe({
        next: (res) => this.handleSaveResponse(res),
        error: (err) => this.handleSaveError(err),
      });
    }
  }

  private handleSaveResponse(res: any): void {
    this.saving = false;
    this.spinner.hide();
    if (res?.status === 200) {
      Swal.fire({ icon: 'success', title: this.apiMsg(res), timer: 2000, showConfirmButton: false });
      if (this.editingEntryId == null) {
        const createdCompanyId = Number(this.entryForm.getRawValue().company_id);
        if (createdCompanyId && createdCompanyId !== this.companyId) {
          this.companyId = createdCompanyId;
          this.loadCustomersForFilter();
        }
      }
      this.closeForm();
      this.loadEntries();
    } else {
      Swal.fire({ icon: 'error', title: this.apiMsg(res) });
    }
  }

  private handleSaveError(err: any): void {
    this.saving = false;
    this.spinner.hide();
    const msg =
      this.curentLang === 'ar'
        ? err?.error?.messageAr
        : this.curentLang === 'he'
          ? err?.error?.messageHe
          : err?.error?.messageEn;
    Swal.fire({ icon: 'error', title: msg || this.translate.instant('customerAccountEntries.errors.saveFailed') });
  }

  confirmDelete(entry: CustomerAccountEntryRow): void {
    Swal.fire({
      icon: 'question',
      title: this.translate.instant('customerAccountEntries.confirmDelete.title'),
      text: this.translate.instant('customerAccountEntries.confirmDelete.text'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('customerAccountEntries.actions.delete'),
      cancelButtonText: this.translate.instant('customerAccountEntries.actions.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteEntry(entry.entry_id);
      }
    });
  }

  private deleteEntry(id: number): void {
    this.spinner.show();
    this.service.deleteCustomerAccountEntry(id).subscribe({
      next: (res) => {
        this.spinner.hide();
        if (res?.status === 200) {
          Swal.fire({ icon: 'success', title: this.apiMsg(res), timer: 2000, showConfirmButton: false });
          this.loadEntries();
        } else {
          Swal.fire({ icon: 'error', title: this.apiMsg(res) });
        }
      },
      error: (err) => {
        this.spinner.hide();
        const msg =
          this.curentLang === 'ar'
            ? err?.error?.messageAr
            : this.curentLang === 'he'
              ? err?.error?.messageHe
              : err?.error?.messageEn;
        Swal.fire({ icon: 'error', title: msg || this.translate.instant('customerAccountEntries.errors.deleteFailed') });
      },
    });
  }

  formatAmount(value: number | null | undefined): string {
    if (value == null) {
      return '—';
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  debitDisplay(entry: CustomerAccountEntryRow): string {
    return entry.entry_type === 'debit' ? this.formatAmount(entry.amount) : '—';
  }

  creditDisplay(entry: CustomerAccountEntryRow): string {
    return entry.entry_type === 'credit' ? this.formatAmount(entry.amount) : '—';
  }

  formatDisplayDate(iso: string): string {
    if (!iso) return '—';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  private validateDateRange(): boolean {
    this.dateRangeError = '';
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.dateRangeError = this.translate.instant('customerAccountEntries.errors.invalidDateRange');
      return false;
    }
    return true;
  }

  private toApiDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private apiMsg(res: any): string {
    if (this.curentLang === 'ar') return res.messageAr || '';
    if (this.curentLang === 'en') return res.messageEn || '';
    return res.messageHe || '';
  }
}
